import React, { useMemo } from 'react';
import { Box, SxProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useMutation } from 'services/api/hooks';
import {
  exportAnalysisData,
  stopAnalysis as stopAnalysisApi
} from 'services/candidate/api';
import { useConfirmationModal } from 'services/modals/hooks';
import { ToastType, useToast } from 'services/toast';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { CandidateBatch } from 'modules/analyse/types/CandidateBatch';
import { CandidateBatchStatus } from 'generated-graphql/types';
import { usePortalUrl } from 'modules/portal/hooks/usePortalUrl';
import { Button } from 'system/atoms';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { useLoadingOverlay } from 'services/loading/hooks';
import { useUpdateCandidateBatchMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { Loading } from 'common/ui/Loading';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import {
  externalIdFields,
  externalIdSchema,
  externalIdSchemaOptional
} from 'common/form/validationSchema/externalId';
import { useAnalyseDetailsCreateTemplate } from 'modules/analyse/hooks/useAnalyseDetailsCreateTemplate';
import { useAnalyseDetailsCreateRuleset } from 'modules/analyse/hooks/useAnalyseDetailsCreateRuleset';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useUser } from 'core/stores/hooks/useUser';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';
import { getCanManageAnalysis } from 'modules/analyse/utils/getCanManageAnalysis';

const container: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
};

type Props = {
  candidateBatch?: CandidateBatch | null;
  setCandidateBatch: (candidateBatch: CandidateBatch) => void;
};

export const CandidatesRankingActions: React.FC<Props> = ({
  candidateBatch,
  setCandidateBatch
}) => {
  const navigate = useNavigate();
  const { openDrawer } = useDrawer();
  const { mutate: stopAnalysisMutation } = useMutation(stopAnalysisApi, {
    successTextMessage: 'Analysis stopped and archived successfully'
  });

  const [updateCandidateBatchMutation] = useUpdateCandidateBatchMutation();
  const { onCreateTemplateClick } = useAnalyseDetailsCreateTemplate();
  const { onCreateRulesetClick } = useAnalyseDetailsCreateRuleset();

  const { showModal } = useConfirmationModal();
  const { showToast } = useToast();
  const { track } = useTracking();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { openModal } = useModalV2();
  const isExternalIdRequiredFlag = useFlag(Flags.EXTERNAL_ID_IS_MANDATORY);
  const isEditExternalIdEnabledFlag = useFlag(Flags.EDIT_EXTERNAL_ID);

  const { isAdmin } = useIsAdmin();
  const user = useUser();
  const canManage = getCanManageAnalysis({
    isAdmin,
    currentUserId: user.id,
    ownerId: candidateBatch?.userId
  });

  const { onCopyPortalUrl, isGeneratingPortalUrl, canCopyPortalUrl } =
    usePortalUrl({
      candidateBatchId: candidateBatch?.id
    });

  const isConnectedToAtsJob = !!candidateBatch?.atsJobId;
  const isLiveStatus = candidateBatch?.status === CandidateBatchStatus.Live;
  const isArchived = candidateBatch?.status === CandidateBatchStatus.Archived;

  const openRulesDrawer = () => {
    openDrawer({
      drawerContentType: DrawerComponentType.AnalysisRulesDrawer,
      drawerComponentProps: {
        candidateBatch,
        setCandidateBatch,
        hideCloseIconButton: true
      }
    });
  };

  const openActionsDrawer = () => {
    openDrawer({
      drawerContentType: DrawerComponentType.AnalysisActionsDrawer,
      drawerComponentProps: {
        candidateBatch,
        setCandidateBatch,
        hideCloseIconButton: true
      }
    });
  };

  const openRequirementsDrawer = () => {
    openDrawer({
      drawerContentType: DrawerComponentType.AnalysisRequirementsDrawer,
      drawerComponentProps: {
        candidateBatch,
        setCandidateBatch,
        hideCloseIconButton: true
      }
    });
  };

  const openRulesetDrawer = () => {
    openDrawer({
      drawerContentType: DrawerComponentType.RulesetDrawer,
      drawerComponentProps: {
        candidateBatchIds: [candidateBatch?.id],
        initialCandidateBatchGroup: candidateBatch?.candidateBatchGroup,
        hideCloseIconButton: true
      }
    });
  };

  const onStopAnalysisClick = () => {
    showModal({
      title: 'Stop & Archive analysis',
      message: `Are you sure you want to stop and archive this analysis? New applications will not be processed and the analysis will be archived.

You will not be able to resume it.`,
      onConfirm: async () => {
        if (!candidateBatch?.id) {
          showToast({ message: 'Something went wrong', type: ToastType.Error });
          return;
        }
        await stopAnalysisMutation({
          candidateBatchId: candidateBatch.id
        });
      }
    });
  };

  const onExportData = async () => {
    try {
      showLoadingOverlay();

      track(TrackingEventName.AnalyseDashboardExport);

      if (candidateBatch?.id) {
        const { url } = await exportAnalysisData({
          candidateBatchId: candidateBatch?.id
        });
        window.open(url, '_blank');
      }
    } finally {
      hideLoadingOverlay();
    }
  };

  const onEditExternalIdClick = async () => {
    openModal({
      title: 'Edit external ID',
      form: {
        schema: isExternalIdRequiredFlag
          ? externalIdSchema
          : externalIdSchemaOptional,
        fields: externalIdFields,
        defaultValues: {
          externalId: candidateBatch?.externalId ?? ''
        }
      },
      onPrimaryAction: async (values) => {
        if (!candidateBatch?.id) {
          showToast({ message: 'Something went wrong', type: ToastType.Error });
          return;
        }

        await updateCandidateBatchMutation({
          variables: {
            input: {
              id: candidateBatch.id,
              externalId: values.externalId || undefined
            }
          }
        });

        showToast({
          title: 'External ID updated',
          type: ToastType.Success
        });
      }
    });
  };

  const moreOptions = useMemo(() => {
    const options: ActionOption[] = [];

    if (!isConnectedToAtsJob) {
      options.push({
        label: 'Add applicants',
        onClick: () => {
          navigate(Paths.ANALYSE_ADD_CV, {
            state: {
              candidateBatchId: candidateBatch?.id
            }
          });
        },
        itemColor: 'primary'
      });
    }

    options.push(
      {
        label: 'Export data',
        onClick: onExportData
      },
      ...(canCopyPortalUrl
        ? [
            {
              label: 'Copy Typeform link',
              loadingLabel: 'Copying',
              isLoading: isGeneratingPortalUrl,
              onClick: onCopyPortalUrl
            }
          ]
        : []),
      {
        label: 'Apply ruleset',
        onClick: openRulesetDrawer
      },
      ...(isEditExternalIdEnabledFlag
        ? [
            {
              label: 'Edit ID',
              onClick: onEditExternalIdClick
            }
          ]
        : []),
      {
        label: 'Create template',
        onClick: () =>
          onCreateTemplateClick({
            title: getAnalysisTitle(candidateBatch),
            candidateBatchId: candidateBatch?.id ?? ''
          })
      },
      {
        label: 'Create ruleset',
        onClick: () =>
          onCreateRulesetClick({
            title: getAnalysisTitle(candidateBatch),
            candidateBatchId: candidateBatch?.id ?? ''
          })
      }
    );

    if (isConnectedToAtsJob && isLiveStatus) {
      options.push({
        label: 'Stop & Archive analysis',
        onClick: onStopAnalysisClick,
        itemColor: 'error'
      });
    }

    return options;
  }, [
    isConnectedToAtsJob,
    navigate,
    candidateBatch?.id,
    candidateBatch?.candidateBatchGroup?.id,
    candidateBatch?.candidateBatchGroup?.name,
    candidateBatch?.externalId,
    candidateBatch?.title,
    candidateBatch?.jobDescriptionParsed?.jobTitle,
    onCreateTemplateClick,
    onCreateRulesetClick,
    onExportData,
    canCopyPortalUrl,
    isGeneratingPortalUrl,
    onCopyPortalUrl,
    openRulesetDrawer,
    isEditExternalIdEnabledFlag,
    onEditExternalIdClick,
    isLiveStatus,
    onStopAnalysisClick
  ]);

  if (isArchived) {
    return null;
  }

  if (!candidateBatch) {
    return <Loading />;
  }

  if (!canManage) {
    return null;
  }

  // The workflow builder owns a workflow-managed batch; only export stays.
  if (candidateBatch.isWorkflowManaged) {
    return (
      <Button variant="secondary" onClick={onExportData}>
        Export data
      </Button>
    );
  }

  return (
    <Box sx={container}>
      <Button variant="secondary" onClick={openRequirementsDrawer}>
        Requirements
      </Button>

      <Button variant="secondary" onClick={openRulesDrawer}>
        Rules
      </Button>

      <Button variant="primary" onClick={openActionsDrawer}>
        Actions
      </Button>

      <ActionPopover
        options={moreOptions}
        id="more-options"
        iconSx={{ color: 'primary.main' }}
      />
    </Box>
  );
};
